import { useCallback, useContext, useMemo, useRef } from 'react'
import { atomStore } from '@figma/jotai'
import { experimentTestState } from '@figma/share-labmate/experiment-test-state'
import { getFeatureFlags } from '@web/lib/feature_flags'
import { usePrevious } from '@web/lib/hooks/use_previous'
import { useAtomValue } from '@web/lib/jotai'

import initialOptions from 'lib/initial_options'

import type { DynamicConfig } from './internals/dynamic_config_primitives'
import { defaultConfig, getStatsigExperimentImpl } from './internals/experiment'
import { StatsigContext } from './internals/statsig_context'
import {
  type StatsigInitState,
  statsigStatusAtom,
  statsigSuspendedAtom,
} from './internals/statsig_state'
import { reportStatsigErrorToSentry } from './internals/telemetry'
import { isAlreadyReportedStatsigInitError } from './internals/types'
import { shouldLoadStatsigSDK } from './internals/utils'

export type GetConfig = () => DynamicConfig

// Experiment Helpers

/**
 * Fetches a specific experiment configuration for a user, awaiting Statsig to reach a steady state.
 * @param experimentName The experiment name to get
 * @returns a promise containing a Statsig experiment configuration object
 */
export async function getStatsigExperiment(
  experimentName: string,
  forceSuspense?: boolean,
  disableExposureLogging: boolean = false,
): Promise<DynamicConfig> {
  try {
    if (experimentTestState.isInstalled()) {
      return getStatsigExperiment_Synchronous(experimentName, disableExposureLogging)
    }
    if ((forceSuspense || getFeatureFlags().statsig_suspend) && !initialOptions().e2e_ci_test) {
      await atomStore.get(statsigStatusAtom).initCompletedDefer.promise
    }
    const statsigStatus = atomStore.get(statsigStatusAtom)

    return getStatsigExperimentImpl(
      statsigStatus,
      experimentName,
      'getExperiment',
      disableExposureLogging,
    )
  } catch (error) {
    if (!isAlreadyReportedStatsigInitError(error)) {
      reportStatsigErrorToSentry(`Error fetching experiment ${experimentName}: ${error}`)
    }
    return defaultConfig
  }
}

/**
 * Synchronous version of getStatsigExperiment. Use in Redux thunks and other non-React contexts.
 * This is safe to use because Statsig is initialized early in the app lifecycle,
 * and by the time user-triggered flows run, Statsig is ready.
 *
 * @param experimentName The experiment name to get
 * @param disableExposureLogging Whether to disable exposure logging
 * @returns a Statsig experiment configuration object
 */
export function getStatsigExperiment_Synchronous(
  experimentName: string,
  disableExposureLogging: boolean = false,
): DynamicConfig {
  const statsigStatus = atomStore.get(statsigStatusAtom)
  return getStatsigExperimentImpl(
    statsigStatus,
    experimentName,
    'getExperiment',
    disableExposureLogging,
  )
}

/**
 * Fetches a specific experiment configuration for a user with exposure logging disabled, awaiting Statsig to reach a steady state.
 */
export function getStatsigExperimentWithoutExposureLogging(
  experimentName: string,
  forceSuspense?: boolean,
): Promise<DynamicConfig> {
  return getStatsigExperiment(experimentName, forceSuspense, true)
}

/**
 * A react hook to fetch a specific experiment configuration for a user. Use
 * this in any React based contexts as it will ensure that the experiment
 * config will be the most up to date by re-rendering on context switches as
 * well as will respect scoped experiment environments.
 *
 * @param experimentName The experiment name to get
 * @returns a function which returns a Statsig experiment configuration object
 */
function useStatsigExperiment_Synchronous(
  experimentName: string,
  disableExposureLogging?: boolean,
): { getConfig: GetConfig } {
  // By pulling in the context here, we trigger an update whenever the parent Statsig context updates.
  // Otherwise, the Statsig user can change and we'd be still be showing treatments for the prior user.
  const { userVersion } = useContext(StatsigContext)
  const previousUserVersion = usePrevious(userVersion)
  // Subscribe to the upgraded init status (the client this reads from) so the
  // hook re-renders and recomputes once init completes.
  const upgradedStatsigStatus = useAtomValue(statsigStatusAtom)
  const cachedExpConfig = useRef<DynamicConfig | null>(null)
  // Invalidate on init state too, not just `userVersion`: a context switch reaches COMPLETED
  // before the deferred `userVersion` bump lands. Identity rather than `.status`, because a
  // switch ends on COMPLETED just as it began.
  const cachedStatusState = useRef<StatsigInitState | null>(null)
  const cachedExperimentTestStateRevision = useRef<number | null>(null)

  const getConfig = useCallback(() => {
    const experimentTestStateRevision = experimentTestState.getRevision()
    if (
      previousUserVersion !== userVersion ||
      cachedStatusState.current !== upgradedStatsigStatus ||
      cachedExpConfig.current == null ||
      cachedExperimentTestStateRevision.current !== experimentTestStateRevision
    ) {
      cachedExpConfig.current = getStatsigExperimentImpl(
        upgradedStatsigStatus,
        experimentName,
        'useExperiment',
        disableExposureLogging,
      )
      cachedStatusState.current = upgradedStatsigStatus
      cachedExperimentTestStateRevision.current = experimentTestStateRevision
    }

    return cachedExpConfig.current
  }, [
    experimentName,
    previousUserVersion,
    upgradedStatsigStatus,
    userVersion,
    disableExposureLogging,
  ])

  // FIXME: This was disabled while enabling the @web/custom-hooks-must-memoize-return-values rule
  // eslint-disable-next-line @web/custom-hooks-must-memoize-return-values
  return { getConfig }
}

/**
 * Like `useStatsigExperiment`, but never suspends while Statsig is initializing.
 *
 * Until `isSettled` is true, `getConfig` gives you a fallback rather than a real assignment: the
 * default config, or a simulator override if one is installed locally. Read it as "no answer yet",
 * not as control.
 *
 * `isSettled` only means "stop waiting", not "init worked". It is also true when init errored,
 * timed out, or will never run, and the fallback is what you get in all of those.
 *
 * Once settled, the memoized reader refreshes on init-state changes, including when a team
 * switch completes before the deferred `userVersion` update.
 */
export function useStatsigExperiment_WithoutSuspense(
  experimentName: string,
  disableExposureLogging?: boolean,
): { getConfig: GetConfig; isSettled: boolean } {
  const { initialized } = useContext(StatsigContext)
  const statsigStatus = useAtomValue(statsigStatusAtom)
  const { getConfig: getSettledConfig } = useStatsigExperiment_Synchronous(
    experimentName,
    disableExposureLogging,
  )
  const isSettled = !shouldLoadStatsigSDK() || initialized

  // Read straight through while unsettled to avoid caching a fallback. This path is never
  // COMPLETED, so it logs no exposure.
  const getConfig = useCallback(
    () =>
      isSettled
        ? getSettledConfig()
        : getStatsigExperimentImpl(
            statsigStatus,
            experimentName,
            'useExperiment',
            disableExposureLogging,
          ),
    [isSettled, getSettledConfig, statsigStatus, experimentName, disableExposureLogging],
  )

  return useMemo(() => ({ getConfig, isSettled }), [getConfig, isSettled])
}

function useStatsigExperiment_Suspended(experimentName: string, disableExposureLogging?: boolean) {
  // Suspend until the upgraded Statsig client is initialized — that's the client
  // the read resolves from. The atom handles rejections internally.
  useAtomValue(statsigSuspendedAtom)
  return useStatsigExperiment_Synchronous(experimentName, disableExposureLogging)
}

export const useStatsigExperiment = getFeatureFlags().statsig_suspend
  ? useStatsigExperiment_Suspended
  : useStatsigExperiment_Synchronous

// Exports
export {
  StatsigInitializeResponseSchema,
  StatsigInitializeResponseV2Schema,
} from './internals/types'
export type { StatsigInitializeResponse, StatsigInitializeResponseV2 } from './internals/types'
export {
  EvaluationReason,
  makeDynamicConfig,
  type DynamicConfig,
} from './internals/dynamic_config_primitives'
export type { Layer } from '@statsig/js-client'

export { statsigStatusAtom, statsigSuspendedAtom }
export { getDynamicConfig, getDynamicConfigFromInitialOptions } from './internals/dynamic_configs'
export {
  useDynamicConfig,
  useDynamicConfigWithSuspense,
} from './internals/hooks/use_dynamic_config'
export { useStatsigFeatureGate } from './internals/hooks/use_statsig_feature_gate'
export {
  checkStatsigFeatureGate,
  manuallyLogStatsigExperimentExposure,
} from './internals/feature_gate'
export {
  frozenStatsigConfig,
  frozenStatsigState__internal,
} from './frozen_configs/statsig_instance'
export { FrozenStatsigState } from './frozen_configs/frozen_statsig_state'
export { WebStatsigDelegate } from './frozen_configs/statsig_web_delegate'
export { defaultConfig } from './internals/experiment'
export { isAutoxpCaptureOverrideActive } from './internals/utils'
